import { useState } from 'react'

import { QueueCarrierDetail } from '#components/QueueCarrierDetail'
import { ReportDataWrapper } from '#components/ReportDataWrapper'
import { Slider } from '#components/Slider'
import { useFetch } from '#hooks/useAdminResource'
import { formatPercentage } from '#utils/format'

interface QueueCarrierBreakdownItem {
  carrierCode: string
  total: number
  scheduled: number
  overdue: number
  unscheduled: number
}

/** Per-carrier queue table served from `endpoint`; rows open the carrier detail */
const QueueCarriers = ({ endpoint }: { endpoint: string }) => {
  const [selected, setSelected] = useState<string>()
  const {
    data,
    isLoading,
    error,
    refetch: refresh,
  } = useFetch<{ carriers: QueueCarrierBreakdownItem[] }>(endpoint, {})

  const carriers = data?.carriers ?? []
  const total = carriers.reduce((sum, carrier) => sum + carrier.total, 0)

  return (
    <ReportDataWrapper
      isLoading={isLoading}
      error={error}
      loadingMessage="Loading carrier breakdown..."
      onRetry={() => refresh()}
    >
      {carriers.length > 0 && (
        <div className="card bg-base-100 shadow-xl">
          <div className="card-body">
            <h2 className="card-title">By Carrier</h2>
            <div className="overflow-x-auto">
              <table className="table table-zebra table-pin-rows">
                <thead>
                  <tr>
                    <th>Carrier</th>
                    <th className="text-right">Subscriptions</th>
                    <th className="text-right">Scheduled</th>
                    <th className="text-right">Overdue</th>
                    <th className="text-right">Not Queued</th>
                    <th className="text-right">Share</th>
                  </tr>
                </thead>
                <tbody>
                  {carriers.map(carrier => (
                    <tr
                      key={carrier.carrierCode}
                      className="hover cursor-pointer"
                      onClick={() => setSelected(carrier.carrierCode)}
                    >
                      <td className="font-medium uppercase">
                        {carrier.carrierCode}
                      </td>
                      <td className="text-right">
                        {carrier.total.toLocaleString()}
                      </td>
                      <td className="text-right text-primary">
                        {carrier.scheduled.toLocaleString()}
                      </td>
                      <td
                        className={`text-right ${carrier.overdue > 0 ? 'text-error' : 'opacity-60'}`}
                      >
                        {carrier.overdue.toLocaleString()}
                      </td>
                      <td className="text-right opacity-60">
                        {carrier.unscheduled.toLocaleString()}
                      </td>
                      <td className="text-right">
                        {formatPercentage(carrier.total, total)}%
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      <Slider
        isOpen={Boolean(selected)}
        onClose={() => setSelected(undefined)}
        title={`${selected?.toUpperCase() ?? ''} Queue Breakdown`}
      >
        {selected && (
          <QueueCarrierDetail endpoint={endpoint} carrierCode={selected} />
        )}
      </Slider>
    </ReportDataWrapper>
  )
}

export { QueueCarriers }
